// libraries
import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import { Carousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import dayjs from 'dayjs'

// components
import Section from '/react4xp/components/Section'
import Fancybox from '/react4xp/components/Fancybox'
import Image from '/react4xp/components/Image'
import RichText from '/react4xp/components/RichText'
import ProductCard from '/react4xp/components/ProductCard'
import Accordion from '/react4xp/components/Accordion'

// svg
import { Download, Expand } from '/react4xp/components/Svg/Ux/Ux'
import { AngleDown } from '/react4xp/components/Svg/Ux/Angle'
import { PadLock } from '/react4xp/components/Svg/Others/Icons'

// utils
import { isNonWhitespaceString, useSmoothScroll } from '/lib/reactutils/utils'

export interface ProductPageProps {
	topPart: {
		title: string
		partNumber?: string
		description?: string
		ctas?: Array<{
			href: string
			text: string
			external?: boolean
		}>
		gallery: Array<{
			srcSmall: string,
			src: string,
			full: string,
			alt: string
		}>
	}
	region?: React.ReactNode
	description?: {
		longDescription?: string
		features?: string
	}
	dimensions?: Array<{
		date: string
		name: string
		size: string
		href: string
		isRestricted?: boolean
	}>
	downloads?: Array<{
		date: string
		type: string
		name: string
		size: string
		href: string
		isRestricted?: boolean
	}>
	spareParts?: Array<{
		href: {
			url: string
			isExternal?: boolean
		}
		img: {
			src: string
			alt: string
		},
		name: string
		partNumber?: string
	}>
	relatedProducts?: Array<{
		href: {
			url: string
			isExternal?: boolean
		}
		img: {
			src: string
			alt: string
		},
		name: string
		partNumber?: string
	}>
	strings: {
		readMore: string // Read product description
	}
}

export default function ProductPage({
	topPart,
	region,
	description,
	dimensions,
	downloads,
	spareParts,
	relatedProducts,
	strings
}: ProductPageProps) {

	// add body class
	useEffect(() => {
		document.body.classList.add('product')
	}, [])

	// smooth scroll (when clicking the anchor links)
	const smoothScrollTo = useSmoothScroll()
	const descriptionRef = useRef(null)

	const smoothScroll = (e) => {
		e.preventDefault()
		const targetId = e.currentTarget.getAttribute('href').substring(1)
		smoothScrollTo(targetId)
		descriptionRef.current.open()
	}

	// filters on the download accordion
	const [selectedFilter, setSelectedFilter] = useState('All')
	const [isDownloadFiltersVisible, setIsDownloadFiltersVisible] = useState(false)

	const toggleDownloadFilters = (filter) => {
		setSelectedFilter(filter)
		setIsDownloadFiltersVisible(false)
	}

	// get all the types inside downloads and create an array
	const downloadTypes = downloads ? Array.from(new Set(downloads.map((item) => item.type))).sort() : []

	// bind a function on click to close the download filters if clicked outside
	const downloadFiltersRef = useRef(null)

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (downloadFiltersRef.current && !downloadFiltersRef.current.contains(event.target)) {
				setIsDownloadFiltersVisible(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	return (
		<>
			<Section
				bgColor='bg-beige-050'
				textColor='text-dark'
				className='product-top-part'
			>
				<div className='grid-container'>
					<div className='grid-row'>

						<div className='c-md-6 c-lg-7 product-top-part__left'>
							{topPart?.gallery && (
								<Fancybox
									options={{
										autoFocus: false,
										dragToClose: false,
										idle: false,
										tpl: {
											closeButton: false,
											main: `
												<div class="fancybox__container product-fancybox" role="dialog" aria-modal="true" aria-label="{{MODAL}}" tabindex="-1">
													
													<button data-fancybox-close class='custom-close'>
														<svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M17.1644 2.25015L2.25015 17.1644L0.835938 15.7502L15.7502 0.835938L17.1644 2.25015Z M2.25015 0.835938L17.1644 15.7502L15.7502 17.1644L0.835938 2.25015L2.25015 0.835938Z' fill='currentColor'/></svg>
													</button>

													<div class="fancybox__backdrop"></div>
													<div class="fancybox__carousel"></div>

													<div class="fancybox__footer-wrapper" id='fancybox-footer-wrapper'>

														<button tabindex="0" title="Previous" class="f-button is-prev" data-carousel-prev="true" id="previous-photo">
															<svg width='20' height='18' viewBox='0 0 20 18' xmlns='http://www.w3.org/2000/svg'><path d='M1.5 10.0003H19.25V8.0003L1.5 8.0003L1.5 10.0003Z M9.91436 15.7502L3.16436 9.00015L9.91436 2.25015L8.50015 0.835938L0.335938 9.00015L8.50015 17.1644L9.91436 15.7502Z' fill='currentColor'/></svg>
														</button>
														
														<div class="fancybox__footer" id='fancybox-thumbs'></div>

														<button tabindex="0" title="Next" class="f-button is-next" data-carousel-next="true" id="next-photo">
															<svg width='20' height='18' viewBox='0 0 20 18' xmlns='http://www.w3.org/2000/svg'><path d='M18.5 10.0003H0.75L0.75 8.0003L18.5 8.0003V10.0003Z M10.0856 15.7502L16.8356 9.00015L10.0856 2.25015L11.4998 0.835938L19.6641 9.00015L11.4998 17.1644L10.0856 15.7502Z' fill='currentColor'/></svg>
														</button>

														<a href='#' download class='download-product-image' id='download-product-image'>
															Download image <span><svg width='16' height='17' viewBox='0 0 16 17' xmlns='http://www.w3.org/2000/svg'><path d='M8.89961 0.600098V13.6501H7.09961V0.600098H8.89961Z M2.37533 6.47705L8.00033 12.1021L13.6253 6.47705L14.8981 7.74984L8.00033 14.6476L1.10254 7.74984L2.37533 6.47705Z M0.224609 14.9751H15.7746V16.7751H0.224609V14.9751Z' fill='currentColor'/></svg></span>
														</a>

													</div>
												</div>
											`,
										},
										backdropClick: false,
										Thumbs: {
											type: 'classic',
											thumbTpl: '<button type="button" aria-label="{{GOTO}}"></button>',
											parentEl: () => document.getElementById('fancybox-thumbs')
										},
										on: {
											done: (_fancybox, slide) => {

												const slidesCount = _fancybox?.userSlides?.length

												// add a class flex after the fancybox is open
												document.getElementById('fancybox-footer-wrapper').classList.add('flex')

												// prev / next arrow buttons
												const previous = document.getElementById('previous-photo')
												const next = document.getElementById('next-photo')
												const thumbs = document.getElementById('fancybox-thumbs')

												// if there is only one slide, hide the buttons and thumbnails
												if (slidesCount <= 1) {
													if (previous) previous.style.display = 'none'
													if (next) next.style.display = 'none'
													if (thumbs) thumbs.style.display = 'none'
												} else {
													if (previous) previous.onclick = () => _fancybox.prev()
													if (next) next.onclick = () => _fancybox.next()
												}

												// download button
												const downloadButton = document.getElementById('download-product-image')

												if (downloadButton && slide.src) {
													(downloadButton as HTMLAnchorElement).href = slide.src
												}
											},

											// update the download image button with the current slide image
											"Carousel.change": (_fancybox, slide, id) => {
												const downloadButton = document.getElementById('download-product-image')

												setTimeout(() => {
													if (downloadButton && slide.slides) {
														(downloadButton as HTMLAnchorElement).href = slide.slides[id].src
													}
												}, 300)
											}
										}
									}}
								>
									<Carousel
										emulateTouch={true}
										infiniteLoop={false}
										swipeable={true}
										animationHandler='slide'
										showThumbs={false}
										showIndicators={true}
										renderIndicator={(onClickHandler, isSelected, index, label) => {
											return (
												<span
													className={clsx('dot', isSelected ? 'selected' : '')}
													onClick={onClickHandler}
													onKeyDown={onClickHandler}
													key={index}
													tabIndex={0}
													aria-label={`${label} ${index + 1}`}
												/>
											)
										}}
										showStatus={false}
										showArrows={true}
										className='product-carousel'
									>

										{topPart?.gallery?.map((item, i) => (
											<div
												key={i}
												className='product-carousel__slide'
											>

												<a
													href={item.full}
													className='product-carousel__zoom'
													data-fancybox='product'
													aria-label='Zoom'
												>
													<Expand />
												</a>

												<div className='product-carousel__image'>
													<Image
														srcSmall={[`${item.srcSmall}`, 375]} // 300x300
														src={item.src} // 600x600
														alt={item.alt}
													/>
												</div>

											</div>
										))}

									</Carousel>
								</Fancybox>
							)}
						</div>

						<div className='c-md-6 c-lg-5 product-top-part__right'>
							<div className='product-top-part__right-wrapper'>

								<div className='top-part'>

									{topPart.title ? <h1 className='top-part__title title-sm'>
										{topPart.title}
									</h1> : <></>}

									<p className='top-part__part-number'>
										Part Number: {topPart.partNumber ? topPart.partNumber : '--'}
									</p>

									{topPart.description ? <div className='top-part__small-desc'>

										<RichText content={topPart.description} />

										{(description?.longDescription || description?.features) ? <a
											href='#description'
											className='read-more'
											onClick={smoothScroll}
										>
											{strings.readMore}
										</a> : <></>}

									</div> : <></>}

								</div>

								<div className='bottom-part'>
									{topPart.ctas?.map((item, i) => (
										<a
											href={item.href}
											target={item.external ? '_blank' : undefined}
											key={i}
										>
											{item.text}
										</a>
									))}
								</div>

							</div>
						</div>

					</div>
				</div>
			</Section>

			{region ? region : <></>}

			<Section
				bgColor='bg-beige-050'
				textColor='text-dark'
				className='product-accordion'
			>
				<div className='grid-container'>

					<div id='description'></div>

					{(description?.longDescription || description?.features) ? (
						<Accordion
							ref={descriptionRef}
							className='product-accordion__description'
							title='Description'
						>
							<div className='grid-row'>

								{description.longDescription ? (
									<div className='c-md-6'>
										<RichText content={description.longDescription} />
									</div>
								) : <></>}

								{description.features ? <div className='c-md-6'>

									<p className='title'>
										Features
									</p>

									<RichText content={description.features} />

								</div> : <></>}

							</div>
						</Accordion>
					) : <></>}

					{dimensions.length > 0 ? (
						<Accordion title='Dimensions' className='product-accordion__dimensions'>
							<div className='table'>

								<div className='table__head'>

									<p className='date'>
										Date
									</p>

									<p className='name'>
										Name
									</p>

									<p className='size'>
										Size
									</p>

									<p className='download'>
										Download
									</p>

								</div>

								{dimensions.length > 0 && dimensions.map((item, i) => (
									<a
										key={i}
										href={item.href}
										download={!item.isRestricted}
										className='table__line'
									>

										<p className='date'>
											{isNonWhitespaceString(item.date) ? dayjs(item.date).format('DD.MM.YY') : '--'}
										</p>

										<p className='name'>
											{isNonWhitespaceString(item.name) ? item.name : '--'}
										</p>

										<p className='size'>
											{isNonWhitespaceString(item.size) ? item.size : '--'}
										</p>

										<span className='download'>
											{item.isRestricted ? (
												<PadLock />
											) : (
												<Download />
											)}
										</span>

									</a>
								))}

							</div>
						</Accordion>
					) : <></>}

					{downloads.length > 0 ? (
						<Accordion title='Downloads' className='product-accordion__downloads'>

							<div className='filter'>

								<p className='filter__title'>
									Documentation type
								</p>

								<div
									ref={downloadFiltersRef}
									className={clsx(
										'filter__wrapper',
										isDownloadFiltersVisible && 'open'
									)}
								>

									<button
										className='filter-select'
										onClick={() => setIsDownloadFiltersVisible(!isDownloadFiltersVisible)}
									>

										<span className='text'>
											{selectedFilter === 'All' ? 'Select' : selectedFilter}
										</span>

										<span className='icon'>
											<AngleDown />
										</span>

									</button>

									<div className='filter-options'>
										<button
											className={clsx(selectedFilter === 'All' && 'active')}
											onClick={() => toggleDownloadFilters('All')}
										>
											All
										</button>

										{downloadTypes.map((item, i) => {
											return item ? (
												<button
													key={i}
													className={clsx(selectedFilter === item && 'active')}
													onClick={() => toggleDownloadFilters(item)}
												>
													{item}
												</button>
											) : <></>
										})}
									</div>

								</div>

							</div>

							{(() => {
								const filteredDownloads = selectedFilter === 'All'
									? downloads
									: downloads.filter(download => download.type === selectedFilter)

								return (
									<div className='table'>
										<div className='table__head'>

											<p className='date'>
												Date
											</p>

											<p className='type'>
												Type
											</p>

											<p className='name'>
												Name
											</p>

											<p className='size'>
												Size
											</p>

											<p className='download'>
												Download
											</p>

										</div>

										{filteredDownloads.length > 0 && filteredDownloads.map((item, i) => (
											<a
												key={i}
												href={item.href}
												download={!item.isRestricted}
												className='table__line'
											>
												<p className='date'>
													{isNonWhitespaceString(item.date) ? dayjs(item.date).format('DD.MM.YY') : '--'}
												</p>

												<p className='type'>
													{isNonWhitespaceString(item.type) ? item.type : '--'}
												</p>

												<p className='name'>
													{isNonWhitespaceString(item.name) ? item.name : '--'}
												</p>

												<p className='size'>
													{isNonWhitespaceString(item.size) ? item.size : '--'}
												</p>

												<span className='download'>
													{item.isRestricted ? (
														<PadLock />
													) : (
														<Download />
													)}
												</span>

											</a>
										))}
									</div>
								)
							})()}
						</Accordion>
					) : <></>}

					{spareParts.length > 0 ? (
						<Accordion title='Accessories and spares' className='product-accordion__spare-parts'>
							<div className='grid'>
								{spareParts.map((item, i) => (
									<ProductCard
										key={i}
										href={{
											url: item.href?.url,
											isExternal: item.href?.isExternal
										}}
										img={{
											src: item.img?.src,
											alt: item.img?.alt
										}}
										name={item.name}
										partNumber={item.partNumber}
									/>
								))}
							</div>
						</Accordion>
					) : <></>}

				</div>
			</Section>

			{relatedProducts.length > 0 ? (
				<Section
					bgColor='bg-beige-050'
					textColor='text-dark'
					className='product-related-products'
				>
					<div className='grid-container'>

						<h2 className='product-related-products__title title-xs'>
							Explore more products
						</h2>

						<div className='product-related-products__grid'>
							<div className='grid-row'>
								{relatedProducts.map((item, i) => (
									<div className='c-6 c-md-3' key={i}>
										<ProductCard
											href={{
												url: item.href?.url,
												isExternal: item.href?.isExternal
											}}
											img={{
												src: item.img?.src,
												alt: item.img?.alt
											}}
											name={item.name}
											partNumber={item.partNumber}
										/>
									</div>
								))}
							</div>
						</div>

					</div>
				</Section>
			) : <></>}

		</>
	)
}